import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { CommonHttpService } from '../../services/common-http/common-http.service';
import { ElectronService } from '../../services/electron/electron.service';
import { environment } from 'src/environments/environment';
import { isAssetStreamUrl } from '../../constants';

/** The only values HTMLMediaElement.preload accepts. */
const VALID_PRELOAD = ['none', 'metadata', 'auto'];

@Component({
    selector: 'app-vplaybook-audio-player',
    templateUrl: './vplaybook-audio-player.component.html',
    styleUrls: ['./vplaybook-audio-player.component.scss'],
    standalone: false
})
export class VplaybookAudioPlayerComponent implements AfterViewInit, OnDestroy {

  primaryUrl: string = '';
  audioSrc: string;
  isPlaying: boolean = false;
  offlineAppPath: any;
  isSecondTime = false;
  onErrorCalled = 0;
  onErrorFuncAllowed = true;

  /** Tracks the in-flight play() so pause() can never race it. */
  private pendingPlay: Promise<void> | null = null;
  private viewReady = false;

  @ViewChild('audioPlayer') audioPlayer: ElementRef<HTMLAudioElement>;
  @Input() audioAssetId: string;
  @Input() isLoadAudio: boolean = true;
  @Input() audioClass: string = '';
  /**
   * Defaults to 'metadata' so the native control can show the clip's duration
   * straight away. With 'none' the player renders with a blank, unusable
   * timeline until the user presses play.
   */
  @Input() preload = 'metadata';
  @Input() onErrorRetry: number = 3;
  @Input() public set setAudioSrc(audioSrc: string) {
    this.audioSrc = audioSrc;
    this.fetchAudioUrl();
  }
  @Input() public set setIsPlaying(isPlaying: boolean) {
    this.isPlaying = isPlaying;
    this.applyPlayState();
  }

  /** Fires when playback actually starts, including via the native controls. */
  @Output() playbackStarted: EventEmitter<void> = new EventEmitter();

  constructor(
    private commonHttpService: CommonHttpService,
    private electronService: ElectronService
  ) {
    this.offlineAppPath = this.electronService.appPath;
  }

  /**
   * Several call sites pass `[preload]="auto"`, which binds a non-existent
   * component field and so arrives as undefined. Fall back to a usable value
   * rather than handing the element garbage.
   */
  get preloadMode(): string {
    return VALID_PRELOAD.includes(this.preload) ? this.preload : 'metadata';
  }

  private get media(): HTMLAudioElement | null {
    return this.audioPlayer?.nativeElement ?? null;
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    // setIsPlaying() may have been applied before the ViewChild resolved.
    this.applyPlayState();
  }

  /**
   * play()/pause() have to be serialized: pausing while a play() promise is
   * still pending rejects it with AbortError, and Safari has historically
   * ignored the pause and kept playing. Let a pending play settle first.
   */
  private safePlay(): void {
    const media = this.media;
    if (!media) {
      return;
    }
    const started = media.play();
    if (started && typeof started.then === 'function') {
      this.pendingPlay = started
        .catch(() => { /* autoplay blocked, or superseded by a pause */ })
        .then(() => { this.pendingPlay = null; });
    }
  }

  private safePause(): void {
    const media = this.media;
    if (!media) {
      return;
    }
    if (this.pendingPlay) {
      this.pendingPlay.then(() => this.media?.pause());
      return;
    }
    media.pause();
  }

  /** Drive the element from `isPlaying` only. Nothing else may start playback. */
  private applyPlayState(): void {
    const media = this.media;
    if (!this.viewReady || !media) {
      return;
    }
    if (this.isPlaying) {
      this.safePlay();
    } else if (this.pendingPlay || !media.paused) {
      this.safePause();
    }
  }

  /** Kept for API compatibility with existing call sites. */
  playAudio(): void {
    this.applyPlayState();
  }

  /**
   * The user can start playback straight from the native controls, so mirror
   * the element's real state rather than trusting the last @Input value.
   */
  onMediaPlay(): void {
    this.isPlaying = true;
    this.playbackStarted.emit();
  }

  onMediaPause(): void {
    this.isPlaying = false;
  }

  fetchAudioUrl(isCurrentTime = false) {
    let currentTime;
    let wasPlaying = false;
    if (isCurrentTime && this.media) {
      currentTime = this.media.currentTime;
      wasPlaying = this.isPlaying;
      this.onErrorCalled++;
      if(this.onErrorCalled === this.onErrorRetry) {
        this.onErrorFuncAllowed = false;
      }
    }
    if(this.audioSrc !== '') {
      if (this.audioSrc.includes('data')
        || this.audioSrc.includes(this.offlineAppPath)
        || this.audioSrc.includes(environment.saasTokenDomain)
        || isAssetStreamUrl(this.audioSrc)) {
        this.primaryUrl = this.audioSrc;
      } else {
        this.commonHttpService.get<any>({
          uri: this.audioSrc,
          message: {
            showMessage: false
          }
        }).subscribe((audioDataUrl) => {
          if (isCurrentTime && this.isSecondTime && this.media) {
            this.reloadAfterError(audioDataUrl, currentTime, wasPlaying);
          } else {
            this.isSecondTime = true;
            this.primaryUrl = audioDataUrl;
          }
        });
      }
    }
  }

  /**
   * Recover from a media error (typically an expired signed URL) by swapping in
   * a fresh one. Only resume playback if it was actually playing -- the old
   * code called play() unconditionally, starting audio nobody asked for.
   * currentTime also has to wait for the new source's metadata, since
   * assigning src resets the element asynchronously.
   */
  private reloadAfterError(audioDataUrl: string, currentTime: number, wasPlaying: boolean): void {
    const media = this.media;
    if (!media) {
      return;
    }
    this.primaryUrl = audioDataUrl;
    media.src = audioDataUrl;
    const restore = () => {
      media.removeEventListener('loadedmetadata', restore);
      if (currentTime > 0 && isFinite(currentTime) && currentTime < media.duration) {
        media.currentTime = currentTime;
      }
      if (wasPlaying) {
        this.safePlay();
      }
    };
    media.addEventListener('loadedmetadata', restore);
    media.load();
  }

  /**
   * Callers hide this player rather than destroying it, so the element would
   * keep streaming. Detach the source on teardown to stop it for good.
   */
  ngOnDestroy(): void {
    const media = this.media;
    if (!media) {
      return;
    }
    media.pause();
    media.removeAttribute('src');
    media.load();
  }
}
